import React, { useState } from 'react';
import { ISearchQuery } from './ISearchQuery';
import { SearchBar } from './SearchBar';
import { MatchingModal } from './MatchingModal';
import { ExpectedWireSearch } from './ExpectedWireSearch';
import { ExpectedCheckSearch } from './ExpectedCheckSearch';
import { ExpectedACHSearch } from './ExpectedACHSearch';
import { eIncomingFundsType } from './eIncomingFundsType';
import { ExpectedOrderSearch } from './ExpectedOrderSearch';
import { IPotentialMatch } from './IPotentialMatch';
import { ConfirmMatch } from './ConfirmMatch';
import { CancelPotentialMatch } from './CancelPotentialMatch';
import { ErrorMessage } from 'ui-toolkit/components/ErrorMessage';
import { eMatchedQueryValue } from 'types/accounting/eMatchedQueryValue';

export interface IMatchingContainerProps {
  intro: string;
  onClose: () => void;
  amountCents: number;
  amountCentsMax: number;
  type: eIncomingFundsType;
  actualId: string;
  accountNumber: string;
  hasEditPermission: boolean;
}

const isMatchable = (potentialMatch: IPotentialMatch, amountCentsMax: number) => {
  const potentialOrder = potentialMatch.expectedOrder;
  if (potentialOrder) {
    return true;
  }

  const potentialExpected = potentialMatch.expectedACH || potentialMatch.expectedCheck || potentialMatch.expectedWire;
  if (potentialExpected) {
    return potentialExpected.amountCents <= amountCentsMax;
  }

  return false;
};

export const MatchingContainer: React.FC<IMatchingContainerProps> = ({
  intro,
  onClose,
  amountCents,
  amountCentsMax,
  type,
  actualId,
  accountNumber,
  hasEditPermission,
}) => {
  const [query, setQuery] = useState<ISearchQuery>({
    amountCents,
    amountCentsMax,
    matched: eMatchedQueryValue.UNMATCHED,
  });
  const [potentialMatch, setPotentialMatch] = useState<IPotentialMatch | undefined>(undefined);

  const amountCentsCheck = !query.amountCents || (query.amountCents && query.amountCentsMax >= query.amountCents);

  const matchable = hasEditPermission && !!potentialMatch && isMatchable(potentialMatch, query.amountCentsMax);
  const matchingMessage = hasEditPermission
    ? 'You do not have permissions to match expecteds to actuals'
    : `The amount of the potential match must be less than the remaining amount of the incoming transaction`;

  const doOrderSearch = query.fileNumber || query.borrowerName || query.propertyAddress;

  return (
    <MatchingModal intro={intro} onClose={onClose}>
      {!amountCentsCheck && (
        <ErrorMessage
          message={`The amount of the expected deposit must be less than the remaining amount of the incoming transaction.`}
        />
      )}
      {potentialMatch && !matchable && <ErrorMessage message={matchingMessage} />}
      {matchable && (
        <ConfirmMatch
          actualId={actualId}
          accountNumber={accountNumber}
          type={type}
          potentialMatch={potentialMatch}
          onClose={onClose}
          amountCents={amountCents}
          amountCentsMax={amountCentsMax}
        />
      )}
      {matchable && <CancelPotentialMatch query={query} cancel={() => setPotentialMatch(undefined)} />}
      {!matchable && (
        <>
          <SearchBar query={query} setQuery={setQuery} />
          <ExpectedWireSearch query={query} accountNumber={accountNumber} setPotentialMatch={setPotentialMatch} />
          <ExpectedCheckSearch query={query} accountNumber={accountNumber} setPotentialMatch={setPotentialMatch} />
          <ExpectedACHSearch query={query} accountNumber={accountNumber} setPotentialMatch={setPotentialMatch} />
          {doOrderSearch && <ExpectedOrderSearch query={query} setPotentialMatch={setPotentialMatch} />}
        </>
      )}
    </MatchingModal>
  );
};
